// RadicalSearchDialog.jsx
import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import {
    codeToPictogram,
    getPictogramsForPackets,
    normalizeCardText,
} from "./RadicalHelpers";
import { getAdvancedSearchCards } from "./RadicalAdvancedMode";

const normalizeSearchText = (value) =>
    normalizeCardText(String(value ?? ""))
        .normalize("NFKD")
        .toLowerCase()
        .replace(/[\s\u0300-\u036f\uFE0E\uFE0F]/g, "");

const RadicalSearchDialog = ({
    open,
    onClose,
    presets,
    pictogramsByPackets,
    onSelect,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [showSymbols, setShowSymbols] = useState(false);

    const searchIndex = useMemo(() => {
        if (!open) return [];

        return presets.map((preset) => {
            const cards =
                preset.recipeType === "advanced"
                    ? getAdvancedSearchCards(preset.data)
                    : Object.entries(preset.cardsByCode || {}).map(
                          ([code, value]) => ({
                              code,
                              value,
                          }),
                      );

            return {
                preset,
                cards: cards.map((card) => ({
                    ...card,
                    searchText: normalizeSearchText(card.value),
                    pictogramCode:
                        card.pictogramCode ??
                        codeToPictogram(
                            card.code,
                            getPictogramsForPackets(
                                preset.packets,
                                pictogramsByPackets,
                            ),
                        ),
                })),
            };
        });
    }, [open, presets, pictogramsByPackets]);

    const symbols = useMemo(
        () => [
            ...new Map(
                Array.from(
                    new Intl.Segmenter("es", {
                        granularity: "grapheme",
                    }).segment(
                        searchIndex
                            .flatMap(({ cards }) =>
                                cards.map((card) => card.pictogramCode),
                            )
                            .join(" "),
                    ),
                    ({ segment }) => [normalizeSearchText(segment), segment],
                ).filter(([value]) => value),
            ).values(),
        ],
        [searchIndex],
    );

    const query = normalizeSearchText(searchQuery);
    const results = query
        ? searchIndex
              .map(({ preset, cards }) => ({
                  preset,
                  matches: cards.filter(
                      (card) =>
                          card.searchText.includes(query) ||
                          normalizeSearchText(card.pictogramCode).includes(
                              query,
                          ),
                  ),
              }))
              .filter(({ matches }) => matches.length)
        : [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Buscar en recetas</DialogTitle>

            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    label="Carta, texto o pictograma"
                    placeholder="K♠, joker, ➡️..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    helperText="Ignora mayúsculas, espacios y tildes."
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        color={
                                            showSymbols ? "primary" : "default"
                                        }
                                        aria-label={
                                            showSymbols
                                                ? "Ocultar símbolos"
                                                : "Mostrar símbolos"
                                        }
                                        aria-expanded={showSymbols}
                                        aria-controls="radical-search-symbols"
                                        onClick={() =>
                                            setShowSymbols(
                                                (previous) => !previous,
                                            )
                                        }
                                    >
                                        <KeyboardIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                {showSymbols && (
                    <Box
                        id="radical-search-symbols"
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            mt: 1,
                        }}
                    >
                        {symbols.map((symbol) => (
                            <Button
                                key={symbol}
                                variant="outlined"
                                onClick={() =>
                                    setSearchQuery(
                                        (previous) => previous + symbol,
                                    )
                                }
                                sx={{
                                    minWidth: 42,
                                    px: 1,
                                    fontSize: 22,
                                    lineHeight: 1.5,
                                }}
                            >
                                {symbol}
                            </Button>
                        ))}
                    </Box>
                )}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ my: 2 }}
                >
                    {query
                        ? `${results.length} recetas encontradas.`
                        : "Escribe una carta o parte del texto guardado."}
                </Typography>
                <Stack spacing={2}>
                    {results.map(({ preset, matches }) => (
                        <Paper
                            key={preset.id}
                            variant="outlined"
                            sx={{ p: 1.5 }}
                        >
                            <Button
                                onClick={() => onSelect(preset)}
                                sx={{
                                    p: 0,
                                    mb: 1,
                                    textTransform: "none",
                                    textAlign: "left",
                                }}
                            >
                                {preset.name}
                                {preset.recipeType === "advanced"
                                    ? " · Avanzada"
                                    : ""}
                            </Button>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(0,1fr) auto minmax(0,1fr)",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {[
                                    "Nombre guardado",
                                    "Código",
                                    "Pictograma",
                                ].map((label) => (
                                    <Typography
                                        key={label}
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {label}
                                    </Typography>
                                ))}

                                {matches.map((card, index) => (
                                    <React.Fragment
                                        key={`${card.code}-${index}`}
                                    >
                                        <Typography
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {String(card.value ?? "")}
                                        </Typography>
                                        <Typography
                                            sx={{ fontFamily: "monospace" }}
                                        >
                                            {card.code}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 22,
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {card.pictogramCode}
                                        </Typography>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RadicalSearchDialog;
